import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import useAction from './useAction';
import './ActionMenu.scss';

const DEFAULT_MENU_OBJECT_PROP = 'menuObject';

const ActionMenuItem = (action, opts) => {
  const { download } = action;
  const Component = opts.MenuItem || MenuItem;
  const options = opts.objectProp || opts.getObject ? opts : { ...opts, objectProp: DEFAULT_MENU_OBJECT_PROP };

  const Item = React.forwardRef((props, ref) => {
    const { onCloseMenu } = props;
    const {
      Icon,
      ariaLabel,
      label,
      actionId,
      onClick,
      url,
      disabled,
    } = useAction(action, props, options);
    const handleClick = useCallback((e) => {
      if (onCloseMenu) {
        onCloseMenu();
      }
      onClick(e);
    }, [onClick, onCloseMenu]);

    return (
      <Component
        component={url ? 'a' : undefined}
        href={url}
        download={download}
        aria-label={ariaLabel}
        onClick={handleClick}
        id={actionId}
        ref={ref}
        className="grb-action-menu-item"
        disabled={disabled}
      >
        {!!Icon && (
          <ListItemIcon className="grb-left-icon">
            <Icon />
          </ListItemIcon>
        )}
        <ListItemText primary={label} />
      </Component>
    );
  });

  Item.propTypes = {
    onCloseMenu: PropTypes.func,
  };

  Item.defaultProps = {
    onCloseMenu: undefined,
  };

  return Item;
};

const ActionMenuItems = (actions, items, opts) => {
  const options = opts.objectProp || opts.getObject ? opts : { ...opts, objectProp: DEFAULT_MENU_OBJECT_PROP };
  const keys = [];
  const Items = items.map((actionName) => {
    const action = actions[actionName];
    if (!action) {
      console.error('No action defined with name ', actionName);
      return null;
    }
    keys.push(actionName);
    return ActionMenuItem(action, options);
  }).filter(x => x);

  return React.forwardRef((props, href) => (
    <React.Fragment>
      {Items.map((Comp, i) => (
        <Comp {...props} href={href} key={keys[i]} />
      ))}
    </React.Fragment>
  ));
};

const ActionMenu = (actions, items, options) => {
  const MenuItems = ActionMenuItems(actions, items, options);
  const { anchorOrigin = { vertical: 'bottom', horizontal: 'center' } } = options;
  const { transformOrigin = { vertical: 'top', horizontal: 'right' } } = options;

  const Component = React.forwardRef(({ anchorEl, onClose, id, anchorOrigin: a, transformOrigin: t, ...rest }, ref) => {
    if (!anchorEl) {
      return (null);
    }
    return (
      <Menu
        id={id}
        anchorEl={anchorEl}
        open
        ref={ref}
        onClose={onClose}
        anchorOrigin={a}
        transformOrigin={t}
        // https://github.com/mui-org/material-ui/issues/10804
        getContentAnchorEl={null}
      >
        <MenuItems {...rest} onCloseMenu={onClose} />
      </Menu>
    );
  });

  Component.propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    anchorEl: PropTypes.any,
    id: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    anchorOrigin: PropTypes.shape({}),
    transformOrigin: PropTypes.shape({}),
  };

  Component.defaultProps = {
    anchorEl: undefined,
    id: undefined,
    anchorOrigin,
    transformOrigin,
  };

  return Component;
};

function useMenu(actions, menuItems, options) {
  const [Component] = useState(() => (options.Menu || ActionMenu(actions, menuItems, options)));
  const [objectProp] = useState(() => (options.menuObjectProp || options.objectProp || DEFAULT_MENU_OBJECT_PROP));
  const [menuProps, setMenuProps] = useState({});

  const onCloseMenu = useCallback(() => {
    setMenuProps({ anchorEl: null });
  }, []);

  const onOpenMenu = useCallback((e, object) => {
    setMenuProps({ anchorEl: e.currentTarget, [objectProp]: object });
  }, []);

  return { Menu: Component, menuProps: { ...menuProps, onClose: onCloseMenu }, onOpenMenu };
}

export { ActionMenuItem, ActionMenuItems, ActionMenu, useMenu };
